// Solutions.js
import React, { useState } from 'react';
import {
  Database,
  Shield,
  Leaf,
  Users,
  CheckCircle2,
  ChevronRight,
  Target,
  Settings,
  Quote,
  ArrowUpRight,
} from 'lucide-react';
import './Solutions.css';

const solutions = [
  {
    id: 'gudwords',
    title: 'Inspirations Platform',
    icon: <Quote />,
    subtitle: 'GudWords.org — quotes to nurture a sustainable mind',
    description:
      "GudWords.org is our inspirations platform — a living collection of great sayings from across the world's cultures and languages, built to inspire people and bring positive thinking, because a sustainable human mind produces sustainable actions.",
    kind: 'product',
    link: 'https://gudwords.org',
    services: [
      'Curated quotes from global philosophical and cultural traditions',
      'Multilingual — discover wisdom across languages',
      'Beautifully designed quote cards to read, save, and share',
      'Subscriptions — inspiration delivered to your inbox',
    ],
    benefits: [
      'Positive Thinking',
      'Daily Inspiration',
      'Cultural Wisdom',
      'Sustainable Mindset',
    ],
  },
  {
    id: 'enterprise-data',
    title: 'Enterprise Data Strategy',
    icon: <Database />,
    subtitle: 'Transform your data into strategic business value',
    description:
      'Align data priorities to long-term business goals with our comprehensive data strategy services.',
    services: [
      'Align data priorities to long-term business goals',
      'Estimate business value and ROI',
      'Create AI-ready, modern data platform blueprint',
      'Develop solution architecture & roadmap',
    ],
    benefits: [
      'Strategic Alignment',
      'ROI Optimization',
      'Future-Ready Architecture',
      'Clear Implementation Path',
    ],
  },
  {
    id: 'data-security',
    title: 'Data Security & Governance',
    icon: <Shield />,
    subtitle: 'Build trust and transparency with modern solutions',
    description:
      'Comprehensive data security, privacy, and governance solutions to protect your most valuable assets.',
    services: [
      'Identify data security, privacy & governance requirements',
      'Fit-gap analysis of top MDM & governance platforms',
      'Recommendation & roadmap development',
      'MDM implementation',
      'Customer data security implementation',
    ],
    technologies: ['Microsoft Purview', 'Profisee', 'Custom Solutions'],
    benefits: [
      'Enhanced Security',
      'Regulatory Compliance',
      'Data Transparency',
      'Risk Mitigation',
    ],
  },
  {
    id: 'esg-reporting',
    title: 'ESG Reporting',
    icon: <Leaf />,
    subtitle: 'Comprehensive ESG data solutions for sustainable reporting',
    description:
      'End-to-end ESG reporting solutions from data collection to advanced analytics and forecasting.',
    grouped: true,
    services: [
      {
        category: 'ESG Data Mart',
        items: [
          'ESG metrics design & data mapping',
          'Data model & metrics design',
          'Data engineering',
        ],
      },
      {
        category: 'ESG BI',
        items: ['BI reports & dashboards design', 'BI implementation'],
      },
      {
        category: 'ESG Analytics',
        items: ['Projections, forecasting and risk analysis'],
      },
      {
        category: 'SaaS Implementation',
        items: [
          'Fabric Sustainability Data Solutions implementation',
          'Customizations / enhancements',
        ],
      },
    ],
    benefits: [
      'Sustainability Insights',
      'Regulatory Reporting',
      'Risk Analysis',
      'Future Planning',
    ],
  },
  {
    id: 'customer-experience',
    title: 'Customer Experience Solutions',
    icon: <Users />,
    subtitle: 'Gain customer intelligence and drive excellence',
    description:
      'Advanced analytics and insights to understand, predict, and enhance customer experiences.',
    services: [
      'Conversational Quiz (in development)',
      'NPS key driver analysis',
      'Customer sentiment analysis (B2B & B2C)',
      'Process mining & feedback insights',
      'People feedback analytics & recommendations',
      'Product feedback synthesis',
      'Brand sentiment analysis (survey / social media)',
      'Topic modeling — identify customer hot topics',
    ],
    benefits: [
      'Customer Intelligence',
      'Experience Optimization',
      'Predictive Insights',
      'Brand Monitoring',
    ],
  },
];

const Solutions = () => {
  const [activeTab, setActiveTab] = useState(0);
  const current = solutions[activeTab];

  return (
    <div className="solutions-page">
      {/* Hero */}
      <section className="page-hero">
        <div className="wrap">
          <span className="eyebrow">Solutions</span>
          <h1>Built for the long term.</h1>
          <p className="page-hero-sub">
            Comprehensive data and analytics solutions designed for sustainable
            business transformation.
          </p>
        </div>
      </section>

      {/* Rail + detail */}
      <section className="sol-body">
        <div className="wrap sol-layout">
          <nav className="sol-rail" aria-label="Solution areas">
            {solutions.map((s, i) => (
              <button
                key={s.id}
                className={`sol-rail-item ${i === activeTab ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
                aria-pressed={i === activeTab}
              >
                <span className="sol-rail-icon">{s.icon}</span>
                <span>
                  <strong>{s.title}</strong>
                  <em>{s.subtitle}</em>
                </span>
              </button>
            ))}
          </nav>

          <article className="sol-detail" key={current.id}>
            <header className="sol-detail-head">
              <span className="sol-detail-icon">{current.icon}</span>
              <div>
                <h2>{current.title}</h2>
                <p>{current.description}</p>
              </div>
            </header>

            <div className="sol-columns">
              <div className="sol-services">
                <h3>
                  <Settings size={17} />{' '}
                  {current.kind === 'product'
                    ? "What's inside"
                    : 'Services included'}
                </h3>
                {current.grouped ? (
                  current.services.map((group) => (
                    <div key={group.category} className="svc-group">
                      <h4>{group.category}</h4>
                      <ul>
                        {group.items.map((item) => (
                          <li key={item}>
                            <ChevronRight size={15} /> {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
                ) : (
                  <ul>
                    {current.services.map((service) => (
                      <li key={service}>
                        <CheckCircle2 size={15} /> {service}
                      </li>
                    ))}
                  </ul>
                )}

                {current.technologies && (
                  <div className="sol-tech">
                    <h4>Technologies & platforms</h4>
                    <div className="tech-tags">
                      {current.technologies.map((tech) => (
                        <span key={tech} className="tech-tag">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {current.link && (
                  <a
                    className="btn btn-solid sol-visit"
                    href={current.link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Visit GudWords <ArrowUpRight size={16} />
                  </a>
                )}
              </div>

              <aside className="sol-benefits">
                <h3>
                  <Target size={17} /> Key benefits
                </h3>
                <ul>
                  {current.benefits.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              </aside>
            </div>
          </article>
        </div>
      </section>

      {/* CTA */}
      <section className="sol-cta on-dark">
        <div className="wrap sol-cta-inner">
          <h2>Ready to transform your business?</h2>
          <p>
            Let's discuss how our solutions can help you achieve sustainable
            growth and competitive advantage through strategic data
            initiatives.
          </p>
          <a
            className="btn btn-ghost"
            href="mailto:info@sustunetech.com?subject=Schedule%20Consultation"
          >
            Schedule a consultation
          </a>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
